import { type SalesMetricV2 } from "../types";

export function makeForecastedRev30({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedRev30";
  const name = isDashboardV2
    ? "Forecasted Revenue (30d)"
    : "Forecasted Attributed Rev (30d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted revenue attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 30 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedRev60({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedRev60";
  const name = isDashboardV2
    ? "Forecasted Revenue (60d)"
    : "Forecasted Attributed Rev (60d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted revenue attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 60 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedRev90({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedRev90";
  const name = isDashboardV2
    ? "Forecasted Revenue (90d)"
    : "Forecasted Attributed Rev (90d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted revenue attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 90 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedTxns30({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedTxns30";
  const name = isDashboardV2
    ? "Forecasted Orders (30d)"
    : "Forecasted Attributed Transactions (30d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted orders attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 30 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedTxns60({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedTxns60";
  const name = isDashboardV2
    ? "Forecasted Orders (60d)"
    : "Forecasted Attributed Transactions (60d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted orders attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 60 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedTxns90({
  attributionModelLabel,
  isDashboardV2,
}: {
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = "forecastedTxns90";
  const name = isDashboardV2
    ? "Forecasted Orders (90d)"
    : "Forecasted Attributed Transactions (90d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted orders attributable to this ad using " +
      `the ${attributionModelLabel} attribution model, within 90 days.` +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedRoas30(): SalesMetricV2 {
  const id = "forecastedRoas30";
  const name = "Forecasted ROAS (30d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted Return on Ad Spend within 30 days." +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedRoas60(): SalesMetricV2 {
  const id = "forecastedRoas60";
  const name = "Forecasted ROAS (60d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted Return on Ad Spend within 60 days." +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}

export function makeForecastedRoas90(): SalesMetricV2 {
  const id = "forecastedRoas90";
  const name = "Forecasted ROAS (90d)";

  return {
    id,
    name,
    fullName: name,
    description:
      "Forecasted Return on Ad Spend within 90 days." +
      "<hr />This is a <b>forecasted metric</b>. Using your historical data, we " +
      "predict the future value of today's sales and marketing efforts.",
    category: "northbeam",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
    isForecast: true,
  };
}
